/**
 * AdminPendingVerification — Revue manuelle des sessions en anomalie (Iter384 Lot B)
 *
 * Affiche les sessions en `pending_verification` (preuve d'échange insuffisante)
 * avec :
 *  • timeline de présence (client/coach join/leave/overlap)
 *  • montant + payment_intent
 *  • extrait transcription
 *  • décision admin : Valider / Refuser (avec toggle remboursement Stripe) / Demander revue
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Loader2, Check, X, RotateCcw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';

const API = getApiUrl();

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminPendingVerification() {
  const { token } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/marketplace/pending-verification?page=${p}&page_size=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Accès refusé');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(page); }, [token, page, load]);

  const openDetail = async (bookingId) => {
    try {
      const res = await fetch(`${API}/admin/marketplace/pending-verification/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Détail introuvable');
      setDetail(await res.json());
    } catch (err) { toast.error(err.message); }
  };

  const handleApprove = async (bookingId) => {
    if (!window.confirm('Valider cette session ? La preuve d\'échange sera considérée comme attestée.')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/marketplace/pending-verification/${bookingId}/approve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      toast.success('Session validée');
      setDetail(null); load(page);
    } catch (err) { toast.error(err.message); } finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/marketplace/pending-verification/${rejectModal.booking_id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ refund: rejectModal.refund, reason: rejectModal.reason }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      toast.success(`Session refusée${json.refund?.ok ? ' · remboursement Stripe OK' : ''}`);
      setRejectModal(null); setDetail(null); load(page);
    } catch (err) { toast.error(err.message); } finally { setActionLoading(false); }
  };

  const handleRequestReview = async () => {
    if (!reviewModal) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/marketplace/pending-verification/${reviewModal.booking_id}/request-review`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: reviewModal.message }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erreur');
      toast.success(`Revue demandée · ${json.emails_sent?.length || 0} email(s) envoyé(s)`);
      setReviewModal(null);
    } catch (err) { toast.error(err.message); } finally { setActionLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F3]" data-testid="admin-pending-verification-page">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E8E2D2]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin/modules" data-testid="admin-pending-back-btn" className="inline-flex items-center gap-1.5 text-[#6B5E48] text-sm hover:text-[#1F2937]">
            <ArrowLeft className="w-4 h-4" /> Retour Dashboard
          </Link>
          <span className="text-[#A89F8A] text-xs">·</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#92710A] font-semibold">Administration · Sessions à vérifier</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-light text-[#1F1A12] mb-1" style={{ fontFamily: 'Georgia, serif' }}>Sessions à vérifier</h1>
            <p className="text-sm text-[#6B5E48]">Sessions passées sans preuve d'échange fiable. Aucun paiement ne part avant validation manuelle.</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-[#FFFCEC] border border-[#E8DDB5] rounded-full">
            <ShieldAlert className="w-4 h-4 text-[#92710A]" />
            <span className="text-sm font-semibold text-[#1F1A12]">{data.total}</span>
            <span className="text-sm text-[#6B5E48]">en attente</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#92710A] animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <div className="bg-white border border-[#E8E2D2] rounded-2xl p-12 text-center">
            <Check className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <p className="text-[#1F1A12] font-medium">Aucune session en attente de vérification.</p>
            <p className="text-sm text-[#6B5E48] mt-1">Toutes les sessions ont une preuve d'échange valide.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E8E2D2] rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#FBF9F3] border-b border-[#E8E2D2]">
                <tr className="text-left text-xs uppercase tracking-wider text-[#6B5E48]">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Coach / Client</th>
                  <th className="px-4 py-3 font-semibold">Preuve</th>
                  <th className="px-4 py-3 font-semibold">Montant</th>
                  <th className="px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F2EDE2]">
                {data.items.map((b) => (
                  <tr key={b.booking_id} className="hover:bg-[#FBF9F3]" data-testid={`pending-row-${b.booking_id}`}>
                    <td className="px-4 py-3 text-sm text-[#1F1A12]">
                      <div>{b.date}</div>
                      <div className="text-xs text-[#A89F8A]">{b.start_time} - {b.end_time}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-[#1F1A12]">{b.coach_name || '—'}</div>
                      <div className="text-xs text-[#6B5E48]">{b.client_name || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        {b.presence_proof.both_joined ? <Check className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-red-500" />}
                        <span className="text-xs text-[#6B5E48]">2 parties</span>
                      </div>
                      <div className="text-xs text-[#A89F8A]">{b.presence_proof.overlap_seconds || 0}s / {b.presence_proof.min_required || '—'}s</div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#1F1A12] tabular-nums">{b.amount || '—'} €</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`view-detail-${b.booking_id}`} onClick={() => openDetail(b.booking_id)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#1F1A12] bg-[#FBF9F3] border border-[#E8E2D2] rounded-full hover:bg-white">
                        Détail <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.total_pages > 1 && (
              <div className="px-4 py-3 flex justify-between items-center border-t border-[#E8E2D2] bg-[#FBF9F3]">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-sm text-[#6B5E48] disabled:opacity-30">← Précédent</button>
                <span className="text-xs text-[#A89F8A]">Page {data.page} / {data.total_pages}</span>
                <button disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} className="text-sm text-[#6B5E48] disabled:opacity-30">Suivant →</button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modal détail */}
      {detail && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E8E2D2] px-5 py-3 flex items-center justify-between">
              <h2 className="font-semibold text-[#1F1A12]">Détail · Session {detail.date}</h2>
              <button onClick={() => setDetail(null)} className="text-[#6B5E48] hover:text-[#1F1A12]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-[#A89F8A] uppercase">Coach</div><div className="text-[#1F1A12]">{detail.coach_name}<br/><span className="text-xs text-[#6B5E48]">{detail.coach_email}</span></div></div>
                <div><div className="text-xs text-[#A89F8A] uppercase">Client</div><div className="text-[#1F1A12]">{detail.client_name}<br/><span className="text-xs text-[#6B5E48]">{detail.client_email}</span></div></div>
                <div><div className="text-xs text-[#A89F8A] uppercase">Montant</div><div className="text-[#1F1A12] tabular-nums">{detail.amount} €</div></div>
                <div><div className="text-xs text-[#A89F8A] uppercase">Origine</div><div className="text-[#1F1A12]">{detail.origin || '—'}</div></div>
              </div>

              <div className="bg-[#FBF9F3] border border-[#E8E2D2] rounded-xl p-4">
                <div className="text-xs uppercase text-[#A89F8A] mb-3">Timeline présence</div>
                <ul className="space-y-1 text-xs">
                  <li>Coach join : <span className="text-[#1F1A12]">{formatDateTime(detail.coach_joined_at)}</span></li>
                  <li>Coach left : <span className="text-[#1F1A12]">{formatDateTime(detail.coach_left_at)}</span></li>
                  <li>Client join : <span className="text-[#1F1A12]">{formatDateTime(detail.client_joined_at)}</span></li>
                  <li>Client left : <span className="text-[#1F1A12]">{formatDateTime(detail.client_left_at)}</span></li>
                  <li className="pt-2 border-t border-[#E8E2D2]">Overlap : <b className="text-[#1F1A12]">{detail.presence_proof.overlap_seconds}s</b> / requis <b>{detail.presence_proof.min_required}s</b></li>
                  <li>Both joined : <b className={detail.presence_proof.both_joined ? 'text-emerald-600' : 'text-red-500'}>{detail.presence_proof.both_joined ? 'oui' : 'non'}</b></li>
                  <li>Enregistrement : <b className={detail.has_recording ? 'text-emerald-600' : 'text-red-500'}>{detail.has_recording ? 'oui' : 'non'}</b></li>
                </ul>
              </div>

              {detail.ai_transcript_excerpt && (
                <div className="bg-[#FBF9F3] border border-[#E8E2D2] rounded-xl p-4">
                  <div className="text-xs uppercase text-[#A89F8A] mb-2">Extrait transcription ({detail.ai_transcript_length} car.)</div>
                  <p className="text-xs text-[#6B5E48] italic">{detail.ai_transcript_excerpt}…</p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E8E2D2]">
                <button data-testid="action-approve" disabled={actionLoading} onClick={() => handleApprove(detail.booking_id)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-emerald-600 text-white hover:brightness-110 disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" /> Valider
                </button>
                <button data-testid="action-reject" disabled={actionLoading} onClick={() => setRejectModal({ booking_id: detail.booking_id, refund: false, reason: '' })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-red-500 text-white hover:brightness-110 disabled:opacity-50">
                  <X className="w-3.5 h-3.5" /> Refuser
                </button>
                <button data-testid="action-request-review" disabled={actionLoading} onClick={() => setReviewModal({ booking_id: detail.booking_id, message: '' })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-[#FBF9F3] border border-[#E8E2D2] text-[#1F1A12] hover:bg-white">
                  <RotateCcw className="w-3.5 h-3.5" /> Demander revue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal refus + toggle remboursement */}
      {rejectModal && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-[#1F1A12]">Refuser cette session ?</h3>
            <textarea data-testid="reject-reason-input" value={rejectModal.reason} onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })} placeholder="Motif (optionnel)…" rows={3} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm focus:outline-none focus:border-[#92710A] focus:bg-white" />
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="reject-refund-toggle" type="checkbox" checked={rejectModal.refund} onChange={(e) => setRejectModal({ ...rejectModal, refund: e.target.checked })} className="mt-1" />
              <div>
                <div className="text-sm font-medium text-[#1F1A12]">Déclencher un remboursement Stripe</div>
                <div className="text-xs text-[#6B5E48]">Si décoché, l'admin devra effectuer le remboursement manuellement.</div>
              </div>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRejectModal(null)} className="px-4 py-2 text-sm text-[#6B5E48] hover:text-[#1F1A12]">Annuler</button>
              <button data-testid="reject-confirm-btn" disabled={actionLoading} onClick={handleReject} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:brightness-110 disabled:opacity-50">
                {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal demande revue */}
      {reviewModal && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setReviewModal(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-[#1F1A12]">Demander une revue</h3>
            <p className="text-xs text-[#6B5E48]">Un email sera envoyé au coach et au client pour les informer que la session est en cours de vérification.</p>
            <textarea data-testid="review-message-input" value={reviewModal.message} onChange={(e) => setReviewModal({ ...reviewModal, message: e.target.value })} placeholder="Message personnalisé (optionnel)…" rows={3} maxLength={1000} className="w-full px-3 py-2 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm focus:outline-none focus:border-[#92710A] focus:bg-white" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setReviewModal(null)} className="px-4 py-2 text-sm text-[#6B5E48] hover:text-[#1F1A12]">Annuler</button>
              <button data-testid="review-confirm-btn" disabled={actionLoading} onClick={handleRequestReview} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] hover:brightness-110 disabled:opacity-50">
                {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
